import { Flex, Box, Grid, Text, ThemeUIStyleObject } from 'theme-ui';
import ProductLink from '~/components/Product/Link';
import ImgLoader from '~/components/Generic/ImgLoader';
import BadgeHighlight from '~/components/Content/Section/FeaturedCard/BadgeHighlight';
import PriceSection from '../Product/Details/PriceSection';
import ColorSwatch from './ColorSwatch';
import KitSavingsTag from '../Kit/Details/KitSavingsTag';
import { Image } from '~/types';
import { useLocation } from '@reach/router';
import { MouseEvent } from 'react';

type Props = {
  isFeatured?: boolean;
  name: string | null;
  slug: string | null;
  collectionSlug: string | null | undefined;
  queryString?: string;
  bgColor?: string;
  calculatedKitSavings?: number;
  forwardSx?: ThemeUIStyleObject;
  wrapperSx?: ThemeUIStyleObject;
  badge?: Queries.BadgeFragment | null;
  load?: boolean;
  currentImage: Image;
  cardMicroloadHighlight?: string;
  price: number | null;
  regularPrice: number | null;
  colorSelect?: boolean;
  title: string | null;
  cdpCopy?: string;
  callback?: (variant: Queries.VariantDetailsFragmentFragment, query: string, event: MouseEvent) => void;
  product?: Queries.ProductCardFragmentFragment;
  renderPrice?: boolean;
  cardDirection?: boolean | null;
};

const GenericProductCard = ({
  isFeatured,
  name,
  slug,
  collectionSlug,
  queryString,
  bgColor,
  calculatedKitSavings,
  forwardSx = {},
  wrapperSx = {},
  badge,
  load = false,
  currentImage,
  cardMicroloadHighlight,
  price,
  regularPrice,
  colorSelect,
  title,
  cdpCopy,
  callback,
  product,
  renderPrice = true,
  cardDirection = false,
}: Props) => {
  const checkGapSize = () => {
    if (isPDP) return '2.4rem';
    if (isFeatured && renderPrice) return ['1.6rem', '2.4rem'];
    if (!isFeatured && renderPrice) return '1.6rem';
    if (isFeatured && !renderPrice) return ['1.2rem', '2rem'];
    return ['1.2rem'];
  };
  const { pathname } = useLocation();
  const regex = /^\/(mens|womens)\/.+/gi;
  const isPDP = regex.test(pathname);

  return (
    <ProductLink
      data-testid="generic-product-card"
      sx={{
        gridColumn: isFeatured ? ['1 span', '-1 / 1'] : '1 span',
        display: ['block', 'flex'], // Because safari is really weird
        width: '100%',
        ...wrapperSx,
      }}
      locationDetail="Product Card"
      productName={name}
      productSlug={slug}
      collectionSlug={collectionSlug}
      qs={queryString}
    >
      <Grid
        sx={{
          // Card scaling based on width of smaller feature cards
          '--card-scaling': isFeatured ? ['100%', 'calc(50vw - (8rem - 2.75rem))', 'min(30vw, 49rem)'] : '100%',
          position: 'relative',
          gridAutoFlow: 'dense',
          minHeight: 'var(--card-scaling)',
          width: '100%', // Because safari is really weird
          alignContent: 'start',
          gridTemplateColumns: isFeatured ? ['1fr', 'repeat(3, 1fr)'] : '1fr',
          gap: 0,
          overflow: 'hidden !important',
          WebkitMaskImage: '-webkit-radial-gradient(white, black)',
          color: 'black',
          backgroundColor: bgColor ?? 'white',
          isolation: 'isolate',
          '.gatsby-image-wrapper': {
            position: 'static',
          },
          ...forwardSx,
        }}
      >
        <Box sx={{ position: 'relative', gridColumn: cardDirection ? '2 span / -1' : '2 span' }}>
          <Box
            className="imageSection"
            sx={{
              top: 0,
              position: 'relative',
              gridColumn: '2 span',
              opacity: load ? 0 : 1,
              overflow: 'hidden',
              pointerEvents: 'none',
              transition: 'opacity 1s ease',
              aspectRatio: ['1/1', isFeatured ? 'auto' : 1],
              height: '100%',
              width: '100%',
            }}
          >
            <ImgLoader
              badge={badge}
              image={currentImage}
              forwardSx={{
                top: 0,
                objectFit: 'cover',
                width: '100%',
                height: '100%',
                position: 'absolute',
                transition: '.3s ease',
              }}
            />
          </Box>
        </Box>
        <Flex
          className="detailSection"
          sx={{
            minHeight: 'var(--card-scaling)',
            gridColumn: '1 span',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: checkGapSize(),
            p: ['1.6rem', isFeatured ? '4rem 4.9rem' : '2.4rem'],
          }}
        >
          <Box
            sx={{
              fontSize: ['2rem', isFeatured ? '2.8rem' : '2.4rem'],
              fontWeight: 'medium',
              lineHeight: '100%',
              height: isFeatured ? ['100%', 'auto'] : '100%',
              letterSpacing: '-.02em',
              textTransform: 'capitalize',
            }}
          >
            <Flex
              sx={{
                flexDirection: [
                  cardMicroloadHighlight && !isPDP ? 'row-reverse' : 'row',
                  isFeatured ? 'column' : cardMicroloadHighlight && !isPDP ? 'row-reverse' : 'row',
                ],
                justifyContent: [
                  cardMicroloadHighlight ? 'space-between' : 'start',
                  isFeatured ? 'center' : cardMicroloadHighlight ? 'space-between' : 'start',
                ],
                width: '100%',
                gap: isFeatured && [0, '1.6rem'],
              }}
            >
              {!!cardMicroloadHighlight && !isPDP && (
                <Box sx={{ flexShrink: 0, pl: !isFeatured && ['1.6rem', '2.4rem'] }}>
                  <BadgeHighlight text={cardMicroloadHighlight} forwardSx={{ textTransform: 'none' }} />
                </Box>
              )}
              {title ? <Text data-testid="generic-product-title" dangerouslySetInnerHTML={{ __html: title }} /> : null}
            </Flex>
            {renderPrice && (
              <Flex sx={{ gap: '.8rem', flexDirection: 'column' }}>
                {/* @ts-ignore */}
                <PriceSection
                  forwardSx={{
                    fontSize: ['2rem', '2.4rem'],
                    fontWeight: 'inherit',
                    gap: '1rem',
                    '> .strikethrough': { color: 'UIGray2', fontSize: 'inherit' },
                  }}
                  price={price}
                  regularPrice={regularPrice}
                />

                {!!calculatedKitSavings && calculatedKitSavings > 0 && (
                  <KitSavingsTag calculatedKitSavings={calculatedKitSavings} />
                )}
              </Flex>
            )}
          </Box>

          {colorSelect && (
            <ColorSwatch callback={callback} product={product} forwardSx={{ '--dots': '1.6rem', gap: '1.2rem' }} />
          )}

          {!!cdpCopy && (
            <Text
              sx={{
                mt: isFeatured && [0, '0.8rem'],
                fontSize: '1.7rem',
                fontWeight: 400,
                lineHeight: 1.4,
              }}
              dangerouslySetInnerHTML={{
                __html: cdpCopy,
              }}
            ></Text>
          )}
        </Flex>
      </Grid>
    </ProductLink>
  );
};
export default GenericProductCard;
